import { defaultScenario } from './defaults';
import { monthlyRateFromAnnual, pct, computeProgressivePIT, solveGrossForNet, sum, realAnnualReturn } from './math';
import { computeEstonianDistributionTax } from './taxModels';
import {
  ComparisonResult,
  JDGInputs,
  ModelResult,
  ScenarioInputs,
  SpzooInputs,
  YearResult
} from './types';

function flagInvariant(warnings: string[], message: string, payload?: Record<string, any>) {
  const detail = payload ? ` | ${JSON.stringify(payload)}` : '';
  const text = `Invariant failed: ${message}${detail}`;
  warnings.push(text);
  console.error(text);
}

function checkPathIntegrity(paths: { label: string; gross: number; net: number; taxes: number }[], warnings: string[]) {
  for (const path of paths) {
    if (![path.gross, path.net, path.taxes].every(Number.isFinite)) {
      flagInvariant(warnings, 'Non-finite value in path', path);
      continue;
    }
    const diff = Math.abs(path.gross - (path.net + path.taxes));
    const tolerance = 0.01 * Math.max(1, path.gross);
    if (diff > tolerance) {
      flagInvariant(warnings, 'Path gross does not equal net + taxes within tolerance', path);
    }
  }
}

function checkFiniteFields(fields: Record<string, number>, warnings: string[]) {
  for (const [key, value] of Object.entries(fields)) {
    if (!Number.isFinite(value)) {
      flagInvariant(warnings, 'Non-finite numeric result', { key, value });
    }
  }
}

function advancePortfolio(
  state: { balance: number; costBasis: number },
  contribution: number,
  monthlyReturn: number,
  monthIndex: number,
  rebalance: 'annual' | 'never',
  capGainsRate: number
) {
  state.balance = (state.balance + contribution) * (1 + monthlyReturn);
  state.costBasis += contribution;

  let capGainsTax = 0;
  if (rebalance === 'annual' && (monthIndex + 1) % 12 === 0) {
    const gain = state.balance - state.costBasis;
    capGainsTax = Math.max(0, gain * capGainsRate);
    state.balance -= capGainsTax;
    state.costBasis = state.balance;
  }
  return capGainsTax;
}

function inflationFactor(mode: 'real' | 'nominal', monthlyInflation: number, monthIndex: number) {
  if (mode === 'nominal') {
    return Math.pow(1 + monthlyInflation, monthIndex);
  }
  return 1;
}

function simulateJDG(
  inputs: ScenarioInputs,
  monthlyReturn: number,
  monthlyInflation: number,
  inflationMode: 'real' | 'nominal'
): ModelResult {
  const { income, bookkeeping, jdg, general } = inputs;
  const months = general.horizonYears * 12;
  const state = { balance: 0, costBasis: 0 };
  const portfolioSeries: number[] = [];
  const taxSeries: number[] = [];
  const contributionSeries: number[] = [];
  const yearly: YearResult[] = [];
  const summaryTaxes = {
    income: 0,
    payroll: 0,
    cit: 0,
    dividend: 0,
    capGains: 0,
    estonianDistribution: 0
  };
  const warnings: string[] = [];

  let jdgContributionYear = 0;
  let spContributionYear = 0;
  let jdgTaxYear = 0;
  let spTaxYear = 0;
  let jdgOwnerCashYear = 0;

  let cumulativeContribution = 0;
  let totalContribution = 0;
  for (let m = 0; m < months; m++) {
    const factor = inflationFactor(inflationMode, monthlyInflation, m);
    const baseIncome = income.incomeCurrency === 'EUR' ? income.monthlyIncome * income.fxRate : income.monthlyIncome;
    const monthIncome = baseIncome * factor;

    const leakage = monthIncome * pct(income.leakagePct);
    const revenue = monthIncome - leakage;

    const ryczaltTax = revenue * pct(jdg.ryczaltPct);
    const zus = jdg.zusAsPercent ? revenue * pct(jdg.zusPercent) : jdg.zusFixed * factor;
    const bookkeepingCost = bookkeeping.jdgBookkeeping * factor;
    const privateLeakage = revenue * pct(jdg.privateLeakagePct);

    const lifeNeed = income.lifeNeedMonthly * factor;
    const investableBeforeLife = revenue - ryczaltTax - zus - bookkeepingCost - privateLeakage;
    if (investableBeforeLife < 0) {
      warnings.push(`Month ${m + 1}: JDG negative cash after tax/fees; increase revenue or reduce costs.`);
    }
    const afterLife = investableBeforeLife - lifeNeed;
    if (afterLife < 0) {
      warnings.push(`Month ${m + 1}: JDG life-need not met; deficit ${afterLife.toFixed(2)} PLN`);
    }
    const buffer = investableBeforeLife * pct(income.bufferPct);
    const contribution = Math.max(afterLife - buffer, 0);
    const ownerCash = Math.max(investableBeforeLife - contribution, 0);

    summaryTaxes.income += ryczaltTax;
    summaryTaxes.payroll += zus;

    const capTax = advancePortfolio(
      state,
      contribution,
      monthlyReturn,
      m,
      jdg.rebalance,
      pct(jdg.capGainsTaxPct)
    );
    summaryTaxes.capGains += capTax;

    cumulativeContribution += contribution;
    totalContribution += contribution;
    portfolioSeries.push(state.balance);
    taxSeries.push(summaryTaxes.income + summaryTaxes.payroll + summaryTaxes.capGains);
    contributionSeries.push(cumulativeContribution);
    jdgContributionYear += contribution;
    jdgTaxYear += ryczaltTax + zus + capTax;
    jdgOwnerCashYear += ownerCash;

    if ((m + 1) % 12 === 0) {
      yearly.push({
        year: (m + 1) / 12,
        jdgContribution: jdgContributionYear,
        spContribution: spContributionYear,
        jdgPortfolioEnd: state.balance,
        spPortfolioEnd: 0,
        jdgTaxes: jdgTaxYear,
        spTaxes: spTaxYear,
        salaryGross: 0,
        dividendsGross: 0,
        loanWithdrawals: 0,
        interestPaid: 0,
        ownerCashReceived: jdgOwnerCashYear
      });
      jdgContributionYear = 0;
      spContributionYear = 0;
      jdgTaxYear = 0;
      spTaxYear = 0;
      jdgOwnerCashYear = 0;
    }
  }

  if (jdg.rebalance === 'never') {
    const gain = state.balance - state.costBasis;
    const finalTax = Math.max(0, gain * pct(jdg.capGainsTaxPct));
    state.balance -= finalTax;
    summaryTaxes.capGains += finalTax;
  }

  const portfolioEndGross = state.balance;
  const gains = Math.max(0, portfolioEndGross - totalContribution);
  const exitCapGainsTax = gains * pct(jdg.capGainsTaxPct);
  const portfolioEndNet = portfolioEndGross - exitCapGainsTax;
  summaryTaxes.capGains += exitCapGainsTax;

  const consumptionDelivered = sum(yearly.map(y => y.ownerCashReceived));
  const jdgIncomeTaxes = summaryTaxes.income + summaryTaxes.payroll;
  const summary = {
    wealthEnd: portfolioEndNet,
    consumptionDelivered,
    combinedWealthAndConsumption: portfolioEndNet + consumptionDelivered,
    monthlyInvestedAvg: totalContribution / months,
    portfolioEndGross,
    portfolioEndNet,
    totalContributions: totalContribution,
    exitTaxes: {
      capGains: exitCapGainsTax
    },
    totalTaxes: summaryTaxes,
    warnings
  };

  if (gains > 0 && exitCapGainsTax > 0 && portfolioEndNet >= portfolioEndGross) {
    flagInvariant(warnings, 'JDG gains should incur exit tax (net < gross)', { gains, portfolioEndNet, portfolioEndGross });
  }
  checkFiniteFields(
    {
      wealthEnd: summary.wealthEnd,
      portfolioEndGross,
      portfolioEndNet,
      totalContribution,
      consumptionDelivered,
      combinedWealth: summary.combinedWealthAndConsumption
    },
    warnings
  );
  checkPathIntegrity(
    [
      {
        label: 'JDG cash to owner (during horizon)',
        gross: consumptionDelivered + jdgIncomeTaxes,
        net: consumptionDelivered,
        taxes: jdgIncomeTaxes
      },
      {
        label: 'JDG final exit payout (to owner)',
        gross: portfolioEndGross,
        net: portfolioEndNet,
        taxes: exitCapGainsTax
      }
    ],
    warnings
  );

  if (taxSeries.length > 0) {
    const totalTaxPaid =
      summaryTaxes.income + summaryTaxes.payroll + summaryTaxes.cit + summaryTaxes.dividend + summaryTaxes.capGains + summaryTaxes.estonianDistribution;
    taxSeries[taxSeries.length - 1] = totalTaxPaid;
  }

  return {
    summary,
    yearly,
    portfolioSeries,
    taxSeries,
    contributionSeries,
    cashflowPaths: [
      {
        label: 'JDG cash to owner (during horizon)',
        gross: consumptionDelivered + jdgIncomeTaxes,
        net: consumptionDelivered,
        taxes: jdgIncomeTaxes
      },
      {
        label: 'JDG final exit payout (to owner)',
        gross: portfolioEndGross,
        net: portfolioEndNet,
        taxes: exitCapGainsTax
      }
    ]
  };
}

function simulateSpzoo(
  inputs: ScenarioInputs,
  monthlyReturn: number,
  monthlyInflation: number,
  inflationMode: 'real' | 'nominal'
): ModelResult {
  const { income, bookkeeping, spzoo, owner, general } = inputs;
  const months = general.horizonYears * 12;
  const state = { balance: 0, costBasis: 0 };
  const portfolioSeries: number[] = [];
  const taxSeries: number[] = [];
  const contributionSeries: number[] = [];
  const yearly: YearResult[] = [];
  const summaryTaxes = {
    income: 0,
    payroll: 0,
    cit: 0,
    dividend: 0,
    capGains: 0,
    estonianDistribution: 0
  };
  const warnings: string[] = [];
  const lifeGapDetails: { month: number; gap: number }[] = [];
  const salaryProfitMonths: number[] = [];
  const negativeCashMonths: number[] = [];
  const warningSet = new Set<string>();
  if (spzoo.citMode === 'estonian' && spzoo.estonian.useSimple) {
    warningSet.add('Estonian simple mode active; detailed CIT/PIT/offset inputs are ignored to keep modes exclusive.');
  }

  let outstandingLoan = 0;
  let loanStartMonth: number | null = null;
  let salaryGrossTotal = 0;
  let dividendGrossTotal = 0;
  let salaryIncomeTaxTotal = 0;
  let salaryHealthTaxTotal = 0;
  let ownerFlowTaxesTotal = 0;
  let ownerCashTotal = 0;
  let jdgContributionYear = 0;
  let spContributionYear = 0;
  let jdgTaxYear = 0;
  let spTaxYear = 0;
  let salaryGrossYear = 0;
  let dividendGrossYear = 0;
  let loanYear = 0;
  let interestYear = 0;
  let ownerCashYear = 0;
  let cumulativeContribution = 0;
  let totalContribution = 0;

  const solveDividendGrossForNet = (targetNet: number) => {
    if (targetNet <= 0) return 0;
    if (spzoo.citMode === 'estonian') {
      let low = 0;
      let high = targetNet * 3 + 1;
      for (let i = 0; i < 40; i++) {
        const mid = (low + high) / 2;
        const { tax } = computeEstonianDistributionTax(mid, spzoo.estonian);
        const net = mid - tax;
        if (net < targetNet) {
          low = mid;
        } else {
          high = mid;
        }
      }
      return high;
    }
    const taxRate = pct(owner.dividendTaxPct);
    if (taxRate >= 1) return targetNet;
    return targetNet / (1 - taxRate);
  };

  for (let m = 0; m < months; m++) {
    const factor = inflationFactor(inflationMode, monthlyInflation, m);
    const baseIncome = income.incomeCurrency === 'EUR' ? income.monthlyIncome * income.fxRate : income.monthlyIncome;
    const monthIncome = baseIncome * factor;
    const leakage = monthIncome * pct(income.leakagePct);
    const revenue = monthIncome - leakage;

    const operatingCosts =
      (bookkeeping.spBookkeeping + bookkeeping.virtualOffice + bookkeeping.otherAdmin + spzoo.toolingSubsMonthly) *
      factor;
    const profitBeforeSalary = revenue - operatingCosts;

    const lifeNeed = income.lifeNeedMonthly * factor;

    let salaryGross = 0;
    let salaryNet = 0;
    let salaryPIT = 0;
    let salaryHealth = 0;

    const maxSalaryAffordable = Math.max(profitBeforeSalary, 0);

    if (owner.allowSalary) {
      if (owner.solverEnabled) {
        const targetNet = (lifeNeed * owner.salaryPortionPct) / 100;
        const solved = solveGrossForNet(targetNet, owner.progressive, owner.minSalaryGross);
        const affordableGross = Math.min(solved.gross, maxSalaryAffordable);
        if (affordableGross < solved.gross - 1e-6) {
          salaryProfitMonths.push(m + 1);
        }
        const payroll = computeProgressivePIT(affordableGross, owner.progressive);
        salaryGross = affordableGross;
        salaryNet = payroll.net;
        salaryPIT = payroll.pit;
        salaryHealth = payroll.health;
      } else {
        const affordableGross = Math.min(Math.max(owner.manualSalaryGross, 0), maxSalaryAffordable);
        if (affordableGross < owner.manualSalaryGross - 1e-6) {
          salaryProfitMonths.push(m + 1);
        }
        salaryGross = affordableGross;
        const payroll = computeProgressivePIT(affordableGross, owner.progressive);
        salaryNet = payroll.net;
        salaryPIT = payroll.pit;
        salaryHealth = payroll.health;
      }
    }

    let profitBeforeCIT = profitBeforeSalary - salaryGross;
    const preDividendGap = Math.max(lifeNeed - salaryNet, 0);
    summaryTaxes.payroll += salaryHealth;
    summaryTaxes.income += salaryPIT;

    let dividendGross = 0;
    let dividendNet = 0;
    let dividendTax = 0;

    let retained = profitBeforeCIT;

    if (spzoo.citMode === 'classic' && retained > 0) {
      const citTax = retained * pct(spzoo.classicCITRatePct);
      summaryTaxes.cit += citTax;
      retained -= citTax;
    }

    if (owner.allowDividends && retained > 0) {
      const desiredNet = owner.solverEnabled ? Math.min(preDividendGap, (lifeNeed * owner.dividendPortionPct) / 100) : 0;
      const manualGross = !owner.solverEnabled && owner.manualDividendGross > 0 ? owner.manualDividendGross : 0;

      if (desiredNet > 0 || manualGross > 0) {
        const grossNeeded = manualGross > 0 ? manualGross : solveDividendGrossForNet(desiredNet);
        dividendGross = Math.min(grossNeeded, retained);
        if (dividendGross < grossNeeded - 1e-6) {
          warningSet.add(
            `Month ${m + 1}: Dividend capped by liquidity; requested ${grossNeeded.toFixed(2)} PLN, paid ${dividendGross.toFixed(2)} PLN.`
          );
        }
        if (spzoo.citMode === 'estonian') {
          const result = computeEstonianDistributionTax(dividendGross, spzoo.estonian);
          dividendTax = result.tax;
          if (result.warnings.length) {
            warnings.push(...result.warnings);
          }
          dividendNet = dividendGross - dividendTax;
        } else {
          const taxRate = pct(owner.dividendTaxPct);
          dividendTax = dividendGross * taxRate;
          dividendNet = dividendGross - dividendTax;
        }
        retained -= dividendGross;
        dividendGrossTotal += dividendGross;
        if (spzoo.citMode === 'estonian') {
          summaryTaxes.estonianDistribution += dividendTax;
        } else {
          summaryTaxes.dividend += dividendTax;
        }
      }
    }

    let loanDraw = 0;
    if (
      owner.allowLoan &&
      owner.loan.lumpSumAmount > 0 &&
      owner.loan.lumpSumYear !== null &&
      Math.round(owner.loan.lumpSumYear * 12) === m + 1 &&
      outstandingLoan < owner.loan.principalLimit
    ) {
      const availableLoan = Math.max(owner.loan.principalLimit - outstandingLoan, 0);
      const lumpSumRaw =
        owner.loan.currency === 'EUR' ? owner.loan.lumpSumAmount * income.fxRate : owner.loan.lumpSumAmount;
      const desired = Math.max(lumpSumRaw, 0);
      loanDraw = Math.min(desired, availableLoan);
      outstandingLoan += loanDraw;
      loanStartMonth = m;
      retained -= loanDraw;
      if (desired > availableLoan) {
        warningSet.add(
          `Loan cap reached; requested ${desired.toFixed(2)} PLN, approved ${loanDraw.toFixed(2)} PLN.`
        );
      }
    }

    if (owner.allowLoan && owner.manualLoanDraw > 0 && outstandingLoan < owner.loan.principalLimit) {
      const availableLoan = Math.max(owner.loan.principalLimit - outstandingLoan, 0);
      const desired = Math.max(owner.manualLoanDraw, 0);
      const manualDraw = Math.min(desired, availableLoan);
      loanDraw += manualDraw;
      outstandingLoan += manualDraw;
      retained -= manualDraw;
      if (loanStartMonth === null && manualDraw > 0) {
        loanStartMonth = m;
      }
      if (desired > availableLoan) {
        warningSet.add(
          `Loan cap reached; requested ${desired.toFixed(2)} PLN, approved ${manualDraw.toFixed(2)} PLN.`
        );
      }
    }

    const monthlyInterestRate = pct(owner.loan.interestRatePct) / 12;
    const interestPayment = outstandingLoan * monthlyInterestRate;
    const interestTax = interestPayment * pct(owner.loan.interestTaxPct);
    const interestNet = interestPayment - interestTax;
    retained -= interestPayment;
    summaryTaxes.income += interestTax;

    // Owner cash bucket aggregates every owner-facing inflow/outflow (salary, dividends, loans, interest, principal).
    let ownerCash = salaryNet + dividendNet + loanDraw + interestNet;

    if (!owner.loan.interestOnly && outstandingLoan > 0) {
      const termMonths = Math.max(Math.round(owner.loan.termYears * 12), 1);
      const monthsElapsed = loanStartMonth === null ? 0 : m - loanStartMonth;
      const remainingMonths = Math.max(termMonths - monthsElapsed - 1, 1);
      const principalPayment = Math.min(outstandingLoan, outstandingLoan / remainingMonths);
      outstandingLoan -= principalPayment;
      retained -= principalPayment;
      ownerCash -= principalPayment;
    }
    salaryGrossTotal += salaryGross;
    salaryIncomeTaxTotal += salaryPIT;
    salaryHealthTaxTotal += salaryHealth;
    salaryGrossYear += salaryGross;
    dividendGrossYear += dividendGross;
    loanYear += loanDraw;
    interestYear += interestPayment;
    ownerCashYear += ownerCash;
    ownerCashTotal += ownerCash;
    ownerFlowTaxesTotal += salaryPIT + salaryHealth + dividendTax + interestTax;

    const finalGap = Math.max(lifeNeed - ownerCash, 0);
    if (finalGap > 1) {
      lifeGapDetails.push({ month: m + 1, gap: finalGap });
    }
    if (retained < 0) {
      negativeCashMonths.push(m + 1);
    }

    const buffer = retained * pct(income.bufferPct);
    const contribution = Math.max(retained - buffer, 0);

    const capTax = advancePortfolio(
      state,
      contribution,
      monthlyReturn,
      m,
      spzoo.rebalance,
      pct(spzoo.companyCapGainsTaxPct)
    );
    summaryTaxes.capGains += capTax;

    cumulativeContribution += contribution;
    totalContribution += contribution;
    portfolioSeries.push(state.balance);
    taxSeries.push(
      summaryTaxes.income +
        summaryTaxes.payroll +
        summaryTaxes.cit +
        summaryTaxes.dividend +
        summaryTaxes.estonianDistribution +
        summaryTaxes.capGains
    );
    contributionSeries.push(cumulativeContribution);

    spContributionYear += contribution;
    spTaxYear += capTax + salaryPIT + salaryHealth + dividendTax + interestTax;

    if ((m + 1) % 12 === 0) {
      yearly.push({
        year: (m + 1) / 12,
        jdgContribution: jdgContributionYear,
        spContribution: spContributionYear,
        jdgPortfolioEnd: 0,
        spPortfolioEnd: state.balance,
        jdgTaxes: jdgTaxYear,
        spTaxes: spTaxYear,
        salaryGross: salaryGrossYear,
        dividendsGross: dividendGrossYear,
        loanWithdrawals: loanYear,
        interestPaid: interestYear,
        ownerCashReceived: ownerCashYear
      });
      jdgContributionYear = 0;
      spContributionYear = 0;
      jdgTaxYear = 0;
      spTaxYear = 0;
      salaryGrossYear = 0;
      dividendGrossYear = 0;
      loanYear = 0;
      interestYear = 0;
      ownerCashYear = 0;
    }
  }

  if (spzoo.rebalance === 'never') {
    const gain = state.balance - state.costBasis;
    const finalCapTax = Math.max(0, gain * pct(spzoo.companyCapGainsTaxPct));
    state.balance -= finalCapTax;
    summaryTaxes.capGains += finalCapTax;
  }

  const formatMonths = (months: number[]) => {
    const firstThree = months.slice(0, 3).join(', ');
    const suffix = months.length > 3 ? ` … +${months.length - 3} more` : '';
    return `${firstThree}${suffix}`;
  };

  if (lifeGapDetails.length) {
    const firstThree = lifeGapDetails
      .slice(0, 3)
      .map(d => `${d.month} (${d.gap.toFixed(2)} PLN)`)
      .join(', ');
    const maxGap = Math.max(...lifeGapDetails.map(d => d.gap));
    const suffix = lifeGapDetails.length > 3 ? ` … +${lifeGapDetails.length - 3} more months` : '';
    warnings.push(
      `Life-need gap (owner cash < life-need) in months ${firstThree}${suffix}; max gap ${maxGap.toFixed(2)} PLN.`
    );
  }

  if (salaryProfitMonths.length) {
    warnings.push(
      `Salary capped by monthly profitability in months ${formatMonths(salaryProfitMonths)}; solver/inputs trimmed to liquidity.`
    );
  }

  if (negativeCashMonths.length) {
    warnings.push(`Company cash negative after decisions in months ${formatMonths(negativeCashMonths)}.`);
  }

  warningSet.forEach(w => warnings.push(w));

  const consumptionDelivered = sum(yearly.map(y => y.ownerCashReceived));
  const portfolioEndGross = state.balance;

  // Final liquidation and owner distribution (forced full exit).
  const companyGains = Math.max(0, portfolioEndGross - totalContribution);
  const companyCapGainsTax = companyGains * pct(spzoo.companyCapGainsTaxPct);
  const companyCashAfterLiquidation = portfolioEndGross - companyCapGainsTax;
  summaryTaxes.capGains += companyCapGainsTax;

  let distributionTax = 0;
  if (spzoo.citMode === 'estonian') {
    if (spzoo.estonian.useSimple) {
      distributionTax = companyCashAfterLiquidation * pct(spzoo.estonian.effectiveDistributionPct);
    } else {
      const result = computeEstonianDistributionTax(companyCashAfterLiquidation, spzoo.estonian);
      distributionTax = result.tax;
      if (result.warnings.length) {
        warnings.push(...result.warnings);
      }
    }
    summaryTaxes.estonianDistribution += distributionTax;
  } else {
    distributionTax = companyCashAfterLiquidation * pct(spzoo.dividendTaxPct);
    summaryTaxes.dividend += distributionTax;
  }

  const portfolioEndNet = companyCashAfterLiquidation - distributionTax;
  const summary = {
    wealthEnd: portfolioEndNet,
    consumptionDelivered,
    combinedWealthAndConsumption: portfolioEndNet + consumptionDelivered,
    monthlyInvestedAvg: totalContribution / months,
    portfolioEndGross,
    portfolioEndNet,
    totalContributions: totalContribution,
    exitTaxes: {
      companyCapGains: companyCapGainsTax,
      distribution: distributionTax
    },
    totalTaxes: summaryTaxes,
    warnings
  };

  const ownerCashPathNet = Math.max(ownerCashTotal, 0);
  const ownerCashPathTaxes = Math.max(ownerFlowTaxesTotal, 0);
  const ownerCashPathGross = ownerCashPathNet + ownerCashPathTaxes;

  if (companyGains > 0 && companyCapGainsTax + distributionTax > 0 && portfolioEndNet >= portfolioEndGross) {
    flagInvariant(warnings, 'Sp. z o.o. gains should incur exit tax (net < gross)', {
      companyGains,
      portfolioEndNet,
      portfolioEndGross
    });
  }

  checkFiniteFields(
    {
      wealthEnd: summary.wealthEnd,
      portfolioEndGross,
      portfolioEndNet,
      totalContribution,
      consumptionDelivered,
      combinedWealth: summary.combinedWealthAndConsumption
    },
    warnings
  );

  checkPathIntegrity(
    [
      {
        label: 'Sp. z o.o. cash to owner (during horizon)',
        gross: ownerCashPathGross,
        net: ownerCashPathNet,
        taxes: ownerCashPathTaxes
      },
      {
        label: 'Final exit payout (to owner)',
        gross: portfolioEndGross,
        net: portfolioEndNet,
        taxes: companyCapGainsTax + distributionTax
      }
    ],
    warnings
  );

  if (taxSeries.length > 0) {
    const totalTaxPaid =
      summaryTaxes.income +
      summaryTaxes.payroll +
      summaryTaxes.cit +
      summaryTaxes.dividend +
      summaryTaxes.capGains +
      summaryTaxes.estonianDistribution;
    taxSeries[taxSeries.length - 1] = totalTaxPaid;
  }

  return {
    summary,
    yearly,
    portfolioSeries,
    taxSeries,
    contributionSeries,
    cashflowPaths: [
      {
        label: 'Sp. z o.o. cash to owner (during horizon)',
        gross: ownerCashPathGross,
        net: ownerCashPathNet,
        taxes: ownerCashPathTaxes
      },
      {
        label: 'Final exit payout (to owner)',
        gross: portfolioEndGross,
        net: portfolioEndNet,
        taxes: companyCapGainsTax + distributionTax
      }
    ]
  };
}

export function simulateComparison(custom?: ScenarioInputs): ComparisonResult {
  const scenario = custom ?? defaultScenario;
  const inflationMode = scenario.general.inflationMode ?? 'real';
  const warnings: string[] = [];
  if (scenario.general.endExitMode !== 'FULL_EXIT_ALWAYS') {
    warnings.push('Full exit enforced at horizon; endExitMode overridden to FULL_EXIT_ALWAYS for retirement comparability.');
  }
  if (!scenario.general.retirementMode) {
    warnings.push('Retirement mode disabled in inputs; simulator still applies full exit for final wealth comparability.');
  }
  if (scenario.general.inflationPct !== 0 && !scenario.general.inflationMode) {
    warnings.push('Inflation mode not selected; defaulting to real-return mode.');
  }

  const monthlyInflation = monthlyRateFromAnnual(scenario.general.inflationPct);
  const annualReturn =
    inflationMode === 'real'
      ? realAnnualReturn(scenario.general.annualReturnPct, scenario.general.inflationPct) * 100
      : scenario.general.annualReturnPct;
  const monthlyReturn = monthlyRateFromAnnual(annualReturn);

  const jdg = simulateJDG(scenario, monthlyReturn, monthlyInflation, inflationMode);
  const spzoo = simulateSpzoo(scenario, monthlyReturn, monthlyInflation, inflationMode);

  const deltaNetWorth = spzoo.summary.wealthEnd - jdg.summary.wealthEnd;
  const deltaCombined = spzoo.summary.combinedWealthAndConsumption - jdg.summary.combinedWealthAndConsumption;
  warnings.push(...jdg.summary.warnings, ...spzoo.summary.warnings);

  let breakevenYear: number | null = null;
  const months = scenario.general.horizonYears * 12;
  for (let i = 0; i < months; i++) {
    if (spzoo.portfolioSeries[i] >= jdg.portfolioSeries[i]) {
      breakevenYear = Math.floor(i / 12) + 1;
      break;
    }
  }

  return {
    jdg,
    spzoo,
    deltaNetWorth,
    deltaCombined,
    breakevenYear,
    warnings
  };
}
